import { types, Instance, flow, getParent, getRoot } from 'mobx-state-tree';
import { io, Socket } from 'socket.io-client';
import api, { getCurrentStytchJWT } from '../services/api';

// MST Models for Chat
const Message = types.model('Message', {
  id: types.identifier, // Use identifier for MST references/maps
  conversationId: types.string,
  userId: types.maybeNull(types.string),
  role: types.enumeration(['user', 'assistant', 'system']),
  content: types.string,
  htmlContent: types.maybeNull(types.string),
  tiptapJson: types.maybeNull(types.frozen()),
  attachments: types.maybeNull(types.frozen()),
  mentions: types.maybeNull(types.frozen()),
  modelUsed: types.string,
  promptTokens: types.number,
  completionTokens: types.number,
  totalTokens: types.number,
  metadata: types.maybeNull(types.frozen()),
  createdAt: types.string,
});

const Creator = types.model('Creator', {
  id: types.string,
  name: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
});

const Conversation = types.model('Conversation', {
  id: types.identifier, // Use identifier for MST Map
  title: types.string,
  visibility: types.enumeration(['private', 'shared']),
  currentModel: types.string,
  systemPrompt: types.maybeNull(types.string),
  totalTokenCount: types.number,
  userId: types.string,
  orgId: types.string,
  folderId: types.maybeNull(types.string),
  lastMessageAt: types.maybeNull(types.string),
  createdAt: types.string,
  updatedAt: types.string,
  creator: types.maybeNull(Creator),
  messageCount: types.maybeNull(types.number),
  modelsUsed: types.maybeNull(types.array(types.string)),
  
  // Active state (mounted/unmounted)
  isActive: types.optional(types.boolean, false),
  messages: types.optional(types.array(Message), []),
});

const Folder = types.model('Folder', {
  id: types.identifier, // Use identifier for MST Map
  name: types.string,
  type: types.enumeration(['private', 'shared']),
  parentFolderId: types.maybeNull(types.string),
  position: types.number,
  createdAt: types.string,
  updatedAt: types.string,
  conversations: types.optional(types.array(types.reference(Conversation)), []),
  subFolders: types.optional(types.array(types.reference(types.late(() => Folder))), []),
});

const ModelInfo = types.model('ModelInfo', {
  provider: types.string,
  model: types.string,
  displayName: types.string,
  maxTokens: types.maybeNull(types.number),
  supportedFeatures: types.model({
    streaming: types.boolean,
    functions: types.boolean,
    vision: types.boolean,
    documents: types.boolean,
  }),
  capabilities: types.maybeNull(types.frozen()),
  enabled: types.optional(types.boolean, true),
});

const GroupedConversations = types.model('GroupedConversations', {
  today: types.optional(types.array(types.reference(Conversation)), []),
  thisWeek: types.optional(types.array(types.reference(Conversation)), []),
  lastWeek: types.optional(types.array(types.reference(Conversation)), []),
  thisMonth: types.optional(types.array(types.reference(Conversation)), []),
  older: types.optional(types.array(types.reference(Conversation)), []),
  inFolders: types.optional(types.array(types.reference(Conversation)), []),
});

/**
 * MST CHAT STORE
 * Manages all chat-related state in a proper MST tree
 * - Conversations can be mounted/unmounted for active state
 * - All UI state is observable and reactive
 * - WebSocket handled through actions
 */
export const ChatStoreMST = types
  .model('ChatStore', {
    // Data Collections
    conversations: types.optional(types.map(Conversation), {}),
    folders: types.optional(types.map(Folder), {}),
    availableModels: types.optional(types.array(ModelInfo), []),
    
    // Grouped Views
    groupedConversations: types.optional(GroupedConversations, {}),
    
    // Active State
    activeConversationId: types.maybeNull(types.string),
    selectedModel: types.optional(types.string, 'google/gemini-2.5-flash'),
    selectedFolderId: types.maybeNull(types.string),
    
    // Loading States
    conversationsLoaded: types.optional(types.boolean, false),
    conversationsLoading: types.optional(types.boolean, false),
    foldersLoaded: types.optional(types.boolean, false),
    foldersLoading: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false),
    isStreamingResponse: types.optional(types.boolean, false),
    
    // UI State
    showFoldersPanel: types.optional(types.boolean, true),
    showDebugInfo: types.optional(types.boolean, false),
    splitViewCollapsed: types.optional(types.boolean, false),
    conversationListWidth: types.optional(types.number, 300),
    searchQuery: types.optional(types.string, ''),
    
    // Error State
    error: types.maybeNull(types.string),
    
    // WebSocket State
    activeViewers: types.optional(types.array(types.string), []),
    typingUsers: types.optional(types.array(types.string), []),
  })
  .volatile(self => ({
    // Non-serializable state (WebSocket, etc.)
    socket: null as Socket | null,
  }))
  .views(self => ({
    get activeConversation() {
      return self.activeConversationId ? self.conversations.get(self.activeConversationId) : null;
    },
    
    get activeMessages() {
      return this.activeConversation?.messages || [];
    },
    
    get messages() {
      // For backward compatibility - return active conversation messages
      return this.activeMessages;
    },
    
    get conversationsArray() {
      return Array.from(self.conversations.values());
    },
    
    get foldersArray() {
      return Array.from(self.folders.values());
    },
    
    get isAuthenticated() {
      const appStore = getParent(self) as any;
      return appStore.auth.isReallyAuthenticated;
    },
    
    get selectedModelCapabilities() {
      const model = self.availableModels.find(m => m.model === self.selectedModel);
      return model?.capabilities || null;
    },
    
    // Set-like interface for typing users (for backward compatibility)
    get typingUsersSet() {
      return {
        add: (userId: string) => {
          if (!self.typingUsers.includes(userId)) {
            self.typingUsers.push(userId);
          }
        },
        delete: (userId: string) => {
          const index = self.typingUsers.indexOf(userId);
          if (index !== -1) {
            self.typingUsers.splice(index, 1);
          }
        },
        has: (userId: string) => self.typingUsers.includes(userId),
        clear: () => self.typingUsers.clear(),
        size: self.typingUsers.length,
        forEach: (callback: (userId: string) => void) => {
          self.typingUsers.forEach(callback);
        }
      };
    }
  }))
  .actions(self => ({
    // Basic setters
    setError(error: string | null) {
      self.error = error;
    },
    
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    
    setStreamingResponse(streaming: boolean) {
      self.isStreamingResponse = streaming;
    },
    
    setSearchQuery(query: string) {
      self.searchQuery = query;
    },
    
    setSelectedModel(model: string) {
      self.selectedModel = model;
    },
    
    setShowFoldersPanel(show: boolean) {
      self.showFoldersPanel = show;
    },
    
    setSelectedFolderId(folderId: string | null) {
      self.selectedFolderId = folderId;
    },
    
    setConversationListWidth(width: number) {
      self.conversationListWidth = width;
    },
    
    toggleSplitView() {
      self.splitViewCollapsed = !self.splitViewCollapsed;
    },
    
    toggleDebugInfo() {
      self.showDebugInfo = !self.showDebugInfo;
    },
    
    // Hydration methods (for workspace endpoint data)
    setConversations(conversations: any[]) {
      console.log(`💬 [ChatStore.setConversations] Setting ${conversations.length} conversations`);
      console.log('📊 [ChatStore.setConversations] Sample conversation:', conversations[0]);
      self.conversations.clear();
      
      conversations.forEach((conv: any, index: number) => {
        try {
          const convData = {
            id: conv.id,
            title: conv.title,
            visibility: conv.visibility || 'private',
            currentModel: conv.currentModel || conv.current_model || 'gemini-pro',
            systemPrompt: conv.systemPrompt || conv.system_prompt || null,
            totalTokenCount: conv.totalTokenCount || conv.total_token_count || 0,
            userId: conv.userId || conv.user_id,
            orgId: conv.orgId || conv.org_id,
            folderId: conv.folderId || conv.folder_id || null,
            lastMessageAt: conv.lastMessageAt || conv.last_message_at || new Date().toISOString(),
            createdAt: conv.createdAt || conv.created_at || new Date().toISOString(),
            updatedAt: conv.updatedAt || conv.updated_at || new Date().toISOString(),
            creator: conv.creator || null,
            messageCount: conv.messageCount || conv.message_count || 0,
            modelsUsed: conv.modelsUsed || conv.models_used || null,
            isActive: false,
            messages: [],
          };
          console.log(`🔹 [ChatStore.setConversations] Putting conversation ${index}:`, convData.id);
          self.conversations.put(convData);
        } catch (err) {
          console.error(`❌ [ChatStore.setConversations] Failed to put conversation ${index}:`, err.message, conv);
          throw err;
        }
      });
      
      self.updateGroupedConversations();
      console.log(`✅ [ChatStore.setConversations] ${self.conversations.size} conversations loaded`);
    },
    
    setFolders(folders: any[]) {
      console.log(`📁 [ChatStore.setFolders] Setting ${folders.length} folders`);
      console.log('📊 [ChatStore.setFolders] Sample folder:', folders[0]);
      self.folders.clear();
      
      folders.forEach((folder: any, index: number) => {
        try {
          // Ensure dates are strings (backend might return Date objects or ISO strings)
          const createdAt = folder.createdAt || folder.created_at;
          const updatedAt = folder.updatedAt || folder.updated_at;
          
          const folderData = {
            id: folder.id,
            name: folder.name,
            type: folder.type || 'private',
            parentFolderId: folder.parentFolderId || folder.parent_folder_id || null,
            position: folder.position || 0,
            createdAt: typeof createdAt === 'string' ? createdAt : new Date(createdAt).toISOString(),
            updatedAt: typeof updatedAt === 'string' ? updatedAt : new Date(updatedAt).toISOString(),
            // Don't include conversations/subFolders - those are references
          };
          console.log(`🔹 [ChatStore.setFolders] Putting folder ${index}:`, folderData.id, 'createdAt type:', typeof folderData.createdAt);
          self.folders.put(folderData);
        } catch (err) {
          console.error(`❌ [ChatStore.setFolders] Failed to put folder ${index}:`, err.message, 'Folder data:', folder);
          throw err;
        }
      });
      console.log(`✅ [ChatStore.setFolders] ${self.folders.size} folders loaded`);
    },
    
    setAvailableModels(models: any[]) {
      console.log(`🤖 [ChatStore.setAvailableModels] Setting ${models.length} available models`);
      self.availableModels.clear();
      models.forEach((model: any) => {
        self.availableModels.push({
          provider: model.provider,
          model: model.model,
          displayName: model.displayName,
          enabled: model.enabled !== false, // Default to true if not specified
          supportedFeatures: model.supportedFeatures || {
            streaming: true,
            vision: false,
            documents: false,
            functions: false
          },
          capabilities: model.capabilities || null,
          maxTokens: model.contextLength || model.maxTokens || null,
        });
      });
      console.log(`✅ [ChatStore.setAvailableModels] ${self.availableModels.length} models loaded`);
      
      // Log models with vision capabilities
      const visionModels = self.availableModels.filter(m => m.capabilities?.supportsVision);
      if (visionModels.length > 0) {
        console.log(`📸 ${visionModels.length} models support vision/images`);
      }
    },
    
    // Mount/Unmount Active Conversations
    mountConversation(conversationId: string) {
      const conversation = self.conversations.get(conversationId);
      if (conversation) {
        conversation.isActive = true;
        self.activeConversationId = conversationId;
        console.log(`🔗 [ChatStore] Mounted conversation: ${conversationId}`);
      }
    },
    
    unmountConversation(conversationId: string) {
      const conversation = self.conversations.get(conversationId);
      if (conversation) {
        conversation.isActive = false;
        // Clear messages to free memory
        conversation.messages.clear();
        console.log(`🔌 [ChatStore] Unmounted conversation: ${conversationId}`);
      }
    },
    
    unmountAllConversations() {
      self.conversations.forEach(conversation => {
        conversation.isActive = false;
        conversation.messages.clear();
      });
      self.activeConversationId = null;
      console.log('🔌 [ChatStore] Unmounted all conversations');
    },
    
    // WebSocket Management
    initializeWebSocket() {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011';
      
      self.socket = io(`${backendUrl}/chat`, {
        transports: ['websocket'],
        autoConnect: false,
        withCredentials: true, // Send cookies for authentication
      });
      
      // Set up event listeners
      self.socket.on('connect', () => {
        console.log('🔌 [ChatStore] Connected to chat WebSocket');
      });
      
      self.socket.on('disconnect', () => {
        console.log('🔌 [ChatStore] Disconnected from chat WebSocket');
      });
      
      self.socket.on('new-message', (data: { message: Message }) => {
        const conversation = self.conversations.get(data.message.conversationId);
        if (conversation && conversation.isActive) {
          conversation.messages.push(data.message);
        }
      });
    },
    
    connectWebSocket() {
      if (!self.isAuthenticated) {
        console.log('🚫 [ChatStore] Not authenticated, skipping WebSocket connection');
        return;
      }
      
      if (self.socket && !self.socket.connected) {
        self.socket.connect();
      }
    },
    
    disconnectWebSocket() {
      if (self.socket?.connected) {
        self.socket.disconnect();
      }
    },
    
    // Data Loading
    loadConversations: flow(function* () {
      // Guard: Don't run if app isn't initialized
      const appStore = getRoot(self) as any;
      if (!appStore.isInitialized) return;
      
      if (self.conversationsLoading || self.conversationsLoaded) {
        return;
      }
      
      if (!self.isAuthenticated) {
        console.log('[ChatStore] Not authenticated, skipping conversations load');
        return;
      }
      
      self.conversationsLoading = true;
      self.error = null;
      
      try {
        const response = yield api.get(`/api/chat/conversations`, 
        );
        
        // Clear existing conversations
        self.conversations.clear();
        
        // Add new conversations with proper transformation
        response.data.forEach((conv: any) => {
          self.conversations.put({
            id: conv.id,
            title: conv.title,
            visibility: conv.visibility || 'private',
            currentModel: conv.current_model || conv.currentModel || 'google/gemini-2.5-flash',
            systemPrompt: conv.system_prompt || conv.systemPrompt || null,
            totalTokenCount: conv.total_token_count || conv.totalTokenCount || 0,
            userId: conv.user_id || conv.userId,
            orgId: conv.org_id || conv.orgId,
            folderId: conv.folder_id || conv.folderId || null,
            lastMessageAt: conv.last_message_at || conv.lastMessageAt || new Date().toISOString(),
            createdAt: conv.created_at || conv.createdAt || new Date().toISOString(),
            updatedAt: conv.updated_at || conv.updatedAt || new Date().toISOString(),
            creator: conv.creator || null,
            messageCount: conv.messageCount || conv.message_count || 0,
            modelsUsed: conv.modelsUsed || conv.models_used || null,
            isActive: false,
            messages: [],
          });
        });
        
        // Update grouped view
        self.updateGroupedConversations();
        
        self.conversationsLoaded = true;
        console.log(`✅ [ChatStore] Loaded ${response.data.length} conversations`);
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to load conversations:', error);
        self.error = 'Failed to load conversations';
        throw error;
      } finally {
        self.conversationsLoading = false;
      }
    }),
    
    loadFolders: flow(function* () {
      // Guard: Don't run if app isn't initialized
      const appStore = getRoot(self) as any;
      if (!appStore.isInitialized) return;
      
      if (self.foldersLoading || self.foldersLoaded) {
        return;
      }
      
      if (!self.isAuthenticated) {
        console.log('[ChatStore] Not authenticated, skipping folders load');
        return;
      }
      
      self.foldersLoading = true;
      
      try {
        const response = yield api.get(`/api/chat/folders`);
        
        // Clear existing folders
        self.folders.clear();
        
        // Add new folders with proper transformation
        response.data.forEach((folder: any) => {
          const createdAt = folder.createdAt || folder.created_at;
          const updatedAt = folder.updatedAt || folder.updated_at;
          
          const folderData = {
            id: folder.id,
            name: folder.name,
            type: folder.type || 'private',
            parentFolderId: folder.parentFolderId || folder.parent_folder_id || null,
            position: folder.position || 0,
            createdAt: typeof createdAt === 'string' ? createdAt : new Date(createdAt).toISOString(),
            updatedAt: typeof updatedAt === 'string' ? updatedAt : new Date(updatedAt).toISOString(),
          };
          self.folders.put(folderData);
        });
        
        self.foldersLoaded = true;
        console.log(`✅ [ChatStore] Loaded ${response.data.length} folders`);
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to load folders:', error);
        self.error = 'Failed to load folders';
      } finally {
        self.foldersLoading = false;
      }
    }),
    
    loadAvailableModels: flow(function* () {
      // Guard: Don't run if app isn't initialized
      const appStore = getRoot(self) as any;
      if (!appStore.isInitialized) return;
      
      if (!self.isAuthenticated) {
        console.log('[ChatStore] Not authenticated, skipping models load');
        return;
      }
      
      try {
        const response = yield api.get(`/api/chat/models/all`, 
        );
        
        // Clear and update models
        self.availableModels.clear();
        response.data.forEach((model: any) => {
          self.availableModels.push(model);
        });
        
        console.log(`✅ [ChatStore] Loaded ${response.data.length} models`);
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to load models:', error);
        self.error = 'Failed to load models';
      }
    }),
    
    // Select and mount a conversation
    selectConversation: flow(function* (conversationId: string) {
      // Unmount previous conversation
      if (self.activeConversationId && self.activeConversationId !== conversationId) {
        self.unmountConversation(self.activeConversationId);
      }
      
      // Mount new conversation
      self.mountConversation(conversationId);
      
      // Load messages for the active conversation
      yield self.loadMessages(conversationId);
      
      // Resume using the model from the last message in this conversation
      const conversation = self.conversations.get(conversationId);
      if (conversation && conversation.messages.length > 0) {
        const lastMessage = conversation.messages[conversation.messages.length - 1];
        if (lastMessage.modelUsed && lastMessage.modelUsed !== 'unknown') {
          self.selectedModel = lastMessage.modelUsed;
          console.log(`🔄 [ChatStore] Resumed model from last message: ${lastMessage.modelUsed}`);
        }
      }
    }),
    
    loadMessages: flow(function* (conversationId: string) {
      const conversation = self.conversations.get(conversationId);
      if (!conversation) return;
      
      try {
        const response = yield api.get(
          `/api/chat/conversations/${conversationId}/messages`,
        );
        
        // Clear existing messages and add new ones
        conversation.messages.clear();
        response.data.forEach((msg: any) => {
          // Transform backend snake_case to MST camelCase
          conversation.messages.push({
            id: msg.id,
            conversationId: msg.conversation_id || msg.conversationId || conversationId,
            userId: msg.user_id || msg.userId,
            role: msg.role,
            content: msg.content,
            htmlContent: msg.html_content || msg.htmlContent || null,
            tiptapJson: msg.tiptap_json || msg.tiptapJson || null,
            attachments: msg.attachments || null,
            mentions: msg.mentions || null,
            modelUsed: msg.model_used || msg.modelUsed || 'unknown',
            promptTokens: msg.prompt_tokens || msg.promptTokens || 0,
            completionTokens: msg.completion_tokens || msg.completionTokens || 0,
            totalTokens: msg.total_tokens || msg.totalTokens || 0,
            metadata: msg.metadata || null,
            createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
          });
        });
        
        console.log(`✅ [ChatStore] Loaded ${response.data.length} messages for conversation ${conversationId}`);
        
      } catch (error) {
        console.error(`❌ [ChatStore] Failed to load messages for ${conversationId}:`, error);
        self.error = 'Failed to load messages';
      }
    }),
    
    // Group conversations by date
    updateGroupedConversations() {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const thisWeekStart = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));
      const lastWeekStart = new Date(thisWeekStart.getTime() - (7 * 24 * 60 * 60 * 1000));
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const groups = {
        today: [],
        thisWeek: [],
        lastWeek: [],
        thisMonth: [],
        older: [],
        inFolders: [],
      } as any;
      
      self.conversationsArray.forEach(conv => {
        const lastMessageDate = conv.lastMessageAt ? new Date(conv.lastMessageAt) : new Date(conv.updatedAt);
        
        if (conv.folderId) {
          groups.inFolders.push(conv.id);
        } else if (lastMessageDate >= today) {
          groups.today.push(conv.id);
        } else if (lastMessageDate >= thisWeekStart) {
          groups.thisWeek.push(conv.id);
        } else if (lastMessageDate >= lastWeekStart) {
          groups.lastWeek.push(conv.id);
        } else if (lastMessageDate >= thisMonthStart) {
          groups.thisMonth.push(conv.id);
        } else {
          groups.older.push(conv.id);
        }
      });
      
      // Update grouped conversations
      Object.keys(groups).forEach(key => {
        (self.groupedConversations as any)[key].replace(groups[key]);
      });
    },
    
    // CRUD Operations
    createConversation: flow(function* (title: string, visibility: 'private' | 'shared' = 'private') {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = yield api.post(`/api/chat/conversations`, {
          title,
          visibility,
          model: self.selectedModel, // Backend expects 'model' not 'currentModel'
        }, {
          toastMessage: 'Conversation created'
        });
        
        // Add the new conversation to the store (transform backend response to MST model)
        const conv = response.data;
        self.conversations.put({
          id: conv.id,
          title: conv.title,
          visibility: conv.visibility || 'private',
          currentModel: conv.current_model || conv.currentModel || 'gemini-pro',
          systemPrompt: conv.system_prompt || conv.systemPrompt || null,
          totalTokenCount: conv.total_token_count || conv.totalTokenCount || 0,
          userId: conv.user_id || conv.userId,
          orgId: conv.org_id || conv.orgId,
          folderId: conv.folder_id || conv.folderId || null,
          lastMessageAt: conv.last_message_at || conv.lastMessageAt || new Date().toISOString(),
          createdAt: conv.created_at || conv.createdAt,
          updatedAt: conv.updated_at || conv.updatedAt,
          creator: conv.creator || null,
          messageCount: conv.messageCount || 0,
          modelsUsed: conv.modelsUsed || null,
          isActive: false,
          messages: [],
        });
        
        // Update grouped view
        self.updateGroupedConversations();
        
        console.log('✅ [ChatStore] Created conversation:', response.data.id);
        return response.data;
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to create conversation:', error);
        self.error = 'Failed to create conversation';
        throw error;
      }
    }),
    
    updateConversation: flow(function* (conversationId: string, updates: any) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = yield api.put(`/api/chat/conversations/${conversationId}`, updates, {
          toastMessage: 'Conversation updated'
        });
        
        // Update the conversation in the store
        const conversation = self.conversations.get(conversationId);
        if (conversation) {
          Object.assign(conversation, response.data);
        }
        
        // Update grouped view
        self.updateGroupedConversations();
        
        console.log('✅ [ChatStore] Updated conversation:', conversationId);
        return response.data;
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to update conversation:', error);
        self.error = 'Failed to update conversation';
        throw error;
      }
    }),
    
    deleteConversation: flow(function* (conversationId: string) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        yield api.delete(`/api/chat/conversations/${conversationId}`, {
          toastMessage: 'Conversation deleted'
        });
        
        // Remove from store
        self.conversations.delete(conversationId);
        
        // Clear active conversation if it was deleted
        if (self.activeConversationId === conversationId) {
          self.activeConversationId = null;
        }
        
        // Update grouped view
        self.updateGroupedConversations();
        
        console.log('✅ [ChatStore] Deleted conversation:', conversationId);
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to delete conversation:', error);
        self.error = 'Failed to delete conversation';
        throw error;
      }
    }),
    
    forkConversation: flow(function* (conversationId: string) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = yield api.post(`/api/chat/conversations/${conversationId}/fork`, {}, {
          toastMessage: 'Conversation forked'
        });
        
        // Add the forked conversation to the store
        self.conversations.put({
          ...response.data,
          isActive: false,
          messages: [],
        });
        
        // Update grouped view
        self.updateGroupedConversations();
        
        console.log('✅ [ChatStore] Forked conversation:', response.data.id);
        return response.data;
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to fork conversation:', error);
        self.error = 'Failed to fork conversation';
        throw error;
      }
    }),
    
    // Folder management methods
    createFolder: flow(function* (name: string, type: 'private' | 'shared' = 'private') {
      console.log('[ChatStore] Creating folder:', name, type);
      // TODO: Implement folder creation API call
      const newFolder = {
        id: `folder-${Date.now()}`,
        name,
        type,
        conversations: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      self.folders.push(newFolder);
      return newFolder;
    }),
    
    deleteFolder: flow(function* (folderId: string) {
      console.log('[ChatStore] Deleting folder:', folderId);
      // TODO: Implement folder deletion API call
      const index = self.folders.findIndex(f => f.id === folderId);
      if (index >= 0) {
        self.folders.splice(index, 1);
      }
    }),
    
    updateFolder: flow(function* (folderId: string, updates: any) {
      console.log('[ChatStore] Updating folder:', folderId, updates);
      // TODO: Implement folder update API call
      const folder = self.folders.find(f => f.id === folderId);
      if (folder) {
        Object.assign(folder, updates);
      }
      return folder;
    }),
    
    duplicateConversation: flow(function* (conversationId: string) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = yield api.post(`/api/chat/conversations/${conversationId}/duplicate`, {}, {
          toastMessage: 'Conversation duplicated'
        });
        
        // Add the duplicated conversation to the store
        self.conversations.put({
          ...response.data,
          isActive: false,
          messages: [],
        });
        
        // Update grouped view
        self.updateGroupedConversations();
        
        console.log('✅ [ChatStore] Duplicated conversation:', response.data.id);
        return response.data;
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to duplicate conversation:', error);
        self.error = 'Failed to duplicate conversation';
        throw error;
      }
    }),
    
    exportConversation: flow(function* (conversationId: string) {
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = yield api.get(`/api/chat/conversations/${conversationId}/export`, 
        );
        
        console.log('✅ [ChatStore] Exported conversation:', conversationId);
        return response.data;
        
      } catch (error) {
        console.error('❌ [ChatStore] Failed to export conversation:', error);
        self.error = 'Failed to export conversation';
        throw error;
      }
    }),
    
    sendTypingStatus(isTyping: boolean) {
      if (self.socket?.connected && self.activeConversationId) {
        self.socket.emit('typing', {
          conversationId: self.activeConversationId,
          isTyping,
        });
      }
    },
    
    // Reset store
    reset() {
      self.conversations.clear();
      self.folders.clear();
      self.availableModels.clear();
      self.activeConversationId = null;
      self.conversationsLoaded = false;
      self.foldersLoaded = false;
      self.isLoading = false;
      self.error = null;
      self.searchQuery = '';
      
      if (self.socket) {
        self.socket.disconnect();
        self.socket = null;
      }
      
      console.log('🔄 [ChatStore] Reset complete');
    },
  }))
  .actions(self => ({
    // Initialize chat store
    initialize: flow(function* () {
      console.log('🚀 [ChatStore] Initializing...');
      
      // Initialize WebSocket
      self.initializeWebSocket();
      
      // Connect if authenticated
      if (self.isAuthenticated) {
        self.connectWebSocket();
        
        // NOTE: Available models are now loaded from workspace endpoint
        // via setAvailableModels() in AppStore.loadWorkspace()
        
        // NOTE: Conversations and folders are now loaded from workspace endpoint
        // via setConversations() and setFolders() in AppStore.loadWorkspace()
      }
      
      console.log('✅ [ChatStore] Initialization complete');
    }),
    
    // Add a message to a conversation (for optimistic updates)
    addMessage(conversationId: string, message: any) {
      console.log(`💬 [ChatStore.addMessage] Adding message to conversation: ${conversationId}`);
      const conversation = self.conversations.get(conversationId);
      if (!conversation) {
        console.error(`❌ [ChatStore.addMessage] Conversation not found: ${conversationId}`);
        return;
      }
      
      // Add message to conversation
      conversation.messages.push({
        id: message.id || `temp-${Date.now()}`,
        conversationId: conversationId,
        userId: message.userId || message.user_id || null,
        role: message.role,
        content: message.content,
        htmlContent: message.htmlContent || message.html_content || null,
        tiptapJson: message.tiptapJson || message.tiptap_json || null,
        attachments: message.attachments || null,
        mentions: message.mentions || null,
        modelUsed: message.modelUsed || message.model_used || self.selectedModel || 'gemini-pro',
        promptTokens: message.promptTokens || message.prompt_tokens || 0,
        completionTokens: message.completionTokens || message.completion_tokens || 0,
        totalTokens: message.totalTokens || message.total_tokens || 0,
        metadata: message.metadata || null,
        createdAt: message.createdAt || message.created_at || new Date().toISOString(),
      });
      
      console.log(`✅ [ChatStore.addMessage] Message added, total messages: ${conversation.messages.length}`);
    },
    
    // Generate conversation title from first message
    generateConversationTitle: flow(function* (conversationId: string) {
      console.log(`🏷️  [ChatStore.generateConversationTitle] Generating title for: ${conversationId}`);
      
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = yield api.post(`/api/chat/conversations/${conversationId}/generate-title`, {});
        
        const conversation = self.conversations.get(conversationId);
        if (conversation && response.data?.title) {
          conversation.title = response.data.title;
          console.log(`✅ [ChatStore.generateConversationTitle] Title generated: ${response.data.title}`);
          return response.data.title;
        }
        
        return null;
      } catch (error) {
        console.error('❌ [ChatStore.generateConversationTitle] Failed:', error);
        return null;
      }
    }),
    
    // Send a message and stream AI response
    sendMessage: flow(function* (conversationId: string, messageContent: string) {
      console.log(`📤 [ChatStore.sendMessage] Sending message to conversation: ${conversationId}`);
      
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      const conversation = self.conversations.get(conversationId);
      if (!conversation) {
        throw new Error('Conversation not found');
      }
      
      try {
        // Add user message optimistically
        const appStore = getRoot(self) as any;
        const userId = appStore.auth?.user?.id;
        
        (self as any).addMessage(conversationId, {
          id: `temp-user-${Date.now()}`,
          role: 'user',
          content: messageContent,
          userId: userId,
        });
        
        // Start streaming response
        self.setStreamingResponse(true);
        
        // Call backend chat endpoint (streaming)
        // Note: Using fetch here instead of axios because we need ReadableStream support
        // But we manually add the Authorization header with Stytch JWT
        const baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011';
        const jwt = getCurrentStytchJWT();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        
        // Add Authorization header if JWT is available
        if (jwt) {
          headers['Authorization'] = `Bearer ${jwt}`;
          console.log('🔑 [ChatStore.sendMessage] Added Stytch JWT to streaming request');
        } else {
          console.warn('⚠️ [ChatStore.sendMessage] No JWT available for streaming request');
        }
        
        const response = yield fetch(
          `${baseUrl}/api/chat/conversations/${conversationId}/chat`,
          {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              message: messageContent,
              model: self.selectedModel || 'google/gemini-2.5-flash',
            }),
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to send message: ${response.statusText}`);
        }
        
        // Handle streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let aiMessageContent = '';
        let aiMessageId = `temp-ai-${Date.now()}`;
        
        // Add AI message placeholder immediately
        (self as any).addMessage(conversationId, {
          id: aiMessageId,
          role: 'assistant',
          content: '',
          userId: null,
        });
        
        if (reader) {
          while (true) {
            const { done, value } = yield reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') {
                  console.log('✅ [ChatStore.sendMessage] Streaming complete');
                  break;
                }
                
                try {
                  const parsed = JSON.parse(data);
                  
                  // Handle different event types from backend
                  if (parsed.type === 'content' && parsed.content) {
                    // Backend sends 'content' not 'content_delta'
                    // Only append if content is not undefined/null
                    aiMessageContent += parsed.content;
                    // Update AI message content
                    const aiMessage = conversation.messages.find(m => m.id === aiMessageId);
                    if (aiMessage) {
                      (aiMessage as any).content = aiMessageContent;
                    }
                  } else if (parsed.type === 'done') {
                    // Backend sends 'done' not 'message_complete'
                    console.log('✅ [ChatStore.sendMessage] Message complete, messageId:', parsed.messageId);
                    // Can't change MST identifier, so remove temp and add real message
                    if (parsed.messageId && parsed.messageId !== aiMessageId) {
                      const tempIndex = conversation.messages.findIndex(m => m.id === aiMessageId);
                      if (tempIndex >= 0) {
                        conversation.messages.splice(tempIndex, 1);
                        conversation.messages.push({
                          id: parsed.messageId,
                          conversationId: conversationId,
                          userId: null,
                          role: 'assistant',
                          content: aiMessageContent,
                          modelUsed: self.selectedModel || 'google/gemini-2.5-flash',
                          promptTokens: 0,
                          completionTokens: 0,
                          totalTokens: 0,
                          metadata: null,
                          createdAt: new Date().toISOString(),
                        });
                      }
                    }
                  } else if (parsed.type === 'error') {
                    console.error('❌ [ChatStore.sendMessage] Backend error:', parsed.error);
                    
                    // If backend provided error content, use it to update the AI message
                    if (parsed.errorContent) {
                      const aiMessage = conversation.messages.find(m => m.id === aiMessageId);
                      if (aiMessage) {
                        (aiMessage as any).content = parsed.errorContent;
                        (aiMessage as any).metadata = { isError: true, errorType: 'provider_error', originalError: parsed.error };
                      }
                    }
                    
                    // If backend provided a messageId (error message saved to DB), replace the temp message
                    if (parsed.messageId) {
                      const tempIndex = conversation.messages.findIndex(m => m.id === aiMessageId);
                      if (tempIndex >= 0) {
                        conversation.messages.splice(tempIndex, 1);
                        // The error message will be loaded from the backend on next sync
                      }
                    }
                    
                    // Set error state but DON'T throw - error message is displayed in chat
                    self.error = `AI Error: ${parsed.error}`;
                    console.log('⚠️ [ChatStore.sendMessage] Error message saved, continuing...');
                  }
                } catch (e) {
                  console.error('Failed to parse SSE data:', e);
                }
              }
            }
          }
        }
        
        console.log('✅ [ChatStore.sendMessage] Message sent successfully');
        
      } catch (error) {
        console.error('❌ [ChatStore.sendMessage] Failed to send message:', error);
        self.error = 'Failed to send message';
        throw error;
      } finally {
        self.setStreamingResponse(false);
      }
    }),
    
    // Send a message with mentions and attachments
    sendMessageWithMentions: flow(function* (
      conversationId: string,
      messageContent: string,
      htmlContent: string,
      tiptapJson: any,
      mentions: Array<{
        type: string;
        id: string;
        label: string;
        metadata?: any;
      }>,
      attachmentIds?: string[],
      attachmentMetadata?: any[], // Full attachment objects for optimistic update
      abortSignal?: AbortSignal // Abort signal for cancelling streaming
    ) {
      console.log(`📤 [ChatStore.sendMessageWithMentions] Sending message with ${mentions.length} mentions and ${attachmentIds?.length || 0} attachments`);
      
      if (!self.isAuthenticated) {
        throw new Error('Not authenticated');
      }
      
      const conversation = self.conversations.get(conversationId);
      if (!conversation) {
        throw new Error('Conversation not found');
      }
      
      try {
        // Add user message optimistically with full attachment metadata
        const appStore = getRoot(self) as any;
        const userId = appStore.auth?.user?.id;
        
        (self as any).addMessage(conversationId, {
          id: `temp-user-${Date.now()}`,
          role: 'user',
          content: messageContent,
          htmlContent: htmlContent,
          tiptapJson: tiptapJson,
          mentions: mentions,
          attachments: attachmentMetadata && attachmentMetadata.length > 0 ? attachmentMetadata : null, // Use provided metadata for optimistic display
          userId: userId,
        });
        
        // Start streaming response
        self.setStreamingResponse(true);
        
        // Call backend chat endpoint (streaming)
        const baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4011';
        const jwt = getCurrentStytchJWT();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        
        if (jwt) {
          headers['Authorization'] = `Bearer ${jwt}`;
          console.log('🔑 [ChatStore.sendMessageWithMentions] Added Stytch JWT');
        } else {
          console.warn('⚠️ [ChatStore.sendMessageWithMentions] No JWT available');
        }
        
        const response = yield fetch(
          `${baseUrl}/api/chat/conversations/${conversationId}/chat`,
          {
            method: 'POST',
            headers,
            credentials: 'include',
            signal: abortSignal, // Add abort signal to cancel request
            body: JSON.stringify({
              message: messageContent,
              htmlContent,
              tiptapJson,
              mentions,
              attachmentIds: attachmentIds || [],
              model: self.selectedModel || 'google/gemini-2.5-flash',
            }),
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to send message: ${response.statusText}`);
        }
        
        // Handle streaming response (same as sendMessage)
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let aiMessageContent = '';
        let aiMessageId = `temp-ai-${Date.now()}`;
        
        // Add AI message placeholder immediately
        (self as any).addMessage(conversationId, {
          id: aiMessageId,
          role: 'assistant',
          content: '',
          userId: null,
        });
        
        if (reader) {
          while (true) {
            const { done, value } = yield reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') {
                  console.log('✅ [ChatStore.sendMessageWithMentions] Streaming complete');
                  break;
                }
                
                try {
                  const parsed = JSON.parse(data);
                  
                  if (parsed.type === 'content' && parsed.content) {
                    aiMessageContent += parsed.content;
                    const aiMessage = conversation.messages.find(m => m.id === aiMessageId);
                    if (aiMessage) {
                      (aiMessage as any).content = aiMessageContent;
                    }
                  } else if (parsed.type === 'done') {
                    console.log('✅ [ChatStore.sendMessageWithMentions] Message complete');
                    if (parsed.messageId && parsed.messageId !== aiMessageId) {
                      const tempIndex = conversation.messages.findIndex(m => m.id === aiMessageId);
                      if (tempIndex >= 0) {
                        conversation.messages.splice(tempIndex, 1);
                        conversation.messages.push({
                          id: parsed.messageId,
                          conversationId: conversationId,
                          userId: null,
                          role: 'assistant',
                          content: aiMessageContent,
                          modelUsed: self.selectedModel || 'google/gemini-2.5-flash',
                          promptTokens: 0,
                          completionTokens: 0,
                          totalTokens: 0,
                          metadata: null,
                          createdAt: new Date().toISOString(),
                        });
                      }
                    }
                  } else if (parsed.type === 'error') {
                    console.error('❌ [ChatStore.sendMessageWithMentions] Error:', parsed.error);
                    if (parsed.errorContent) {
                      const aiMessage = conversation.messages.find(m => m.id === aiMessageId);
                      if (aiMessage) {
                        (aiMessage as any).content = parsed.errorContent;
                        (aiMessage as any).metadata = { isError: true };
                      }
                    }
                    self.error = `AI Error: ${parsed.error}`;
                  }
                } catch (e) {
                  console.error('Failed to parse SSE data:', e);
                }
              }
            }
          }
        }
        
        console.log('✅ [ChatStore.sendMessageWithMentions] Message sent successfully');
        
      } catch (error) {
        console.error('❌ [ChatStore.sendMessageWithMentions] Failed:', error);
        self.error = 'Failed to send message';
        throw error;
      } finally {
        self.setStreamingResponse(false);
      }
    }),
    
    // WebSocket patch handlers for real-time updates
    addConversationFromWebSocket(data: any) {
      console.log(`📥 [ChatStore] Adding conversation from WebSocket:`, data.id);
      if (self.conversations.has(data.id)) {
        console.log(`⏭️  [ChatStore] Conversation already exists, skipping`);
        return;
      }
      
      const conv = {
        id: data.id,
        title: data.title,
        visibility: data.visibility || 'private',
        currentModel: data.current_model || data.currentModel || 'gemini-pro',
        systemPrompt: data.system_prompt || data.systemPrompt || null,
        totalTokenCount: data.total_token_count || data.totalTokenCount || 0,
        userId: data.user_id || data.userId,
        orgId: data.org_id || data.orgId,
        folderId: data.folder_id || data.folderId || null,
        lastMessageAt: data.last_message_at || data.lastMessageAt || new Date().toISOString(),
        createdAt: data.created_at || data.createdAt || new Date().toISOString(),
        updatedAt: data.updated_at || data.updatedAt || new Date().toISOString(),
        creator: data.creator || null,
        messageCount: data.messageCount || 0,
        modelsUsed: data.modelsUsed || null,
        isActive: false,
        messages: [],
      };
      
      self.conversations.put(conv);
      self.updateGroupedConversations();
    },
    
    updateConversationFromWebSocket(data: any) {
      console.log(`📝 [ChatStore] Updating conversation from WebSocket:`, data.id);
      const conversation = self.conversations.get(data.id);
      if (!conversation) {
        console.warn(`⚠️  [ChatStore] Conversation not found: ${data.id}`);
        return;
      }
      
      // Apply updates
      if (data.title) conversation.title = data.title;
      if (data.visibility) conversation.visibility = data.visibility;
      if (data.current_model || data.currentModel) {
        conversation.currentModel = data.current_model || data.currentModel;
      }
      
      self.updateGroupedConversations();
    },
    
    removeConversationFromWebSocket(conversationId: string) {
      console.log(`🗑️  [ChatStore] Removing conversation from WebSocket:`, conversationId);
      self.conversations.delete(conversationId);
      self.updateGroupedConversations();
    },
    
    addMessageFromWebSocket(data: any) {
      console.log(`💬 [ChatStore] Adding message from WebSocket to conversation:`, data.conversationId);
      const conversation = self.conversations.get(data.conversationId);
      if (!conversation) {
        console.warn(`⚠️  [ChatStore] Conversation not found: ${data.conversationId}`);
        return;
      }
      
      // Only add if not already present (avoid duplicates)
      const exists = conversation.messages.some(m => m.id === data.id);
      if (!exists) {
        conversation.messages.push({
          id: data.id,
          conversationId: data.conversationId || data.conversation_id,
          userId: data.userId || data.user_id || null,
          role: data.role,
          content: data.content,
          modelUsed: data.modelUsed || data.model_used,
          promptTokens: data.promptTokens || data.prompt_tokens || 0,
          completionTokens: data.completionTokens || data.completion_tokens || 0,
          totalTokens: data.totalTokens || data.total_tokens || 0,
          metadata: data.metadata || null,
          createdAt: data.createdAt || data.created_at,
        });
      }
    },
  }));

export type IChatStoreMST = Instance<typeof ChatStoreMST>;
export type IConversation = Instance<typeof Conversation>;
export type IFolder = Instance<typeof Folder>;
export type IGroupedConversations = Instance<typeof GroupedConversations>;
// ChatStoreMST is already exported above as a const
export { Conversation, Folder, GroupedConversations };
